import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatScheduledDate } from "app/components/Dashboard/kanbanDisplay";

const MODAL_STYLE = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  maxWidth: "92vw",
  bgcolor: "background.paper",
  border: "1px solid #e3e6ee",
  boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
  p: 4,
  borderRadius: "10px",
  outline: "none",
  zIndex: 1,
};

export type PendingDeliveryAreaMove = {
  picking: any;
  fromStageId: number | string;
  fromStageTitle: string;
  toStageId: number | string;
  toStageTitle: string;
};

type MoveDeliveryAreaModalProps = {
  open: boolean;
  pendingMove: PendingDeliveryAreaMove | null;
  isSubmitting: boolean;
  userTimezone: string;
  embedded?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export default function MoveDeliveryAreaModal({
  open,
  pendingMove,
  isSubmitting,
  userTimezone,
  embedded = false,
  onConfirm,
  onCancel,
}: MoveDeliveryAreaModalProps) {
  const picking = pendingMove?.picking;

  return (
    <Modal
      open={open}
      disableEnforceFocus={embedded}
      disableAutoFocus={embedded}
      disableRestoreFocus={embedded}
      disableScrollLock
      sx={{
        zIndex: 1400,
        pointerEvents: open ? "auto" : "none",
      }}
      onClose={(_event, reason) => {
        if (isSubmitting) {
          return;
        }
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
      aria-labelledby="move-delivery-area-title"
    >
      <Box sx={MODAL_STYLE}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            id="move-delivery-area-title"
            variant="h6"
            sx={{ fontWeight: 700, color: "#1f2937" }}
          >
            Move delivery area?
          </Typography>
          <IconButton
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Close"
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ color: "#6b7280", mb: 2.5 }}>
          Are you sure you want to move this picking order to a different
          delivery area?
        </Typography>

        {picking && (
          <Box
            sx={{
              bgcolor: "#f8fafc",
              border: "1px solid #e5e9ef",
              borderRadius: "10px",
              p: 2,
              mb: 2.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#1f2937", mb: 0.5 }}
            >
              {picking.name}
            </Typography>
            {picking.partner_id?.name && (
              <Typography variant="body2" sx={{ color: "#374151", mb: 0.25 }}>
                {picking.partner_id.name}
              </Typography>
            )}
            {picking.scheduled_date && (
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                {formatScheduledDate(picking.scheduled_date, userTimezone)}
              </Typography>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            p: 1.5,
            borderRadius: "10px",
            bgcolor: "#eef7f1",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              From
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "#374151", wordBreak: "break-word" }}
            >
              {pendingMove?.fromStageTitle}
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ color: "#5fa67a", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              To
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "#374151", wordBreak: "break-word" }}
            >
              {pendingMove?.toStageTitle}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              bgcolor: "#5fa67a",
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              "&:hover": { bgcolor: "#4d9164" },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Confirm move"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
